import {AxiosRequestConfig, AxiosResponse} from 'axios';
import {CareTask} from '../backend/interfaces/care-task';
import {CareTaskComplementary} from '../backend/interfaces/care-task-complementary';
import {PutEntityCareTask} from '../backend/requests/put-entity-care-task';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';
import {CareTaskNotDoneReason} from '../backend/enums/care-task-not-done-reason';
import {CareTaskData} from '../backend/interfaces/care-task-data';
import {CareTaskFeature} from '../backend/enums/care-task-feature';
import {Attachment} from '../backend/interfaces/attachment';

export class EntityUserCareTasksRepository {
  // Singleton Instance
  private static _instance: EntityUserCareTasksRepository;

  // Private
  private logger: Logger;

  static get instance(): EntityUserCareTasksRepository {
    if (!this._instance) {
      this._instance = new EntityUserCareTasksRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('EntityUserCarePlansRepository');
  }

  public async GetCareTasksWithComplementaryInformation(
    entityId: string,
    userId: string,
    startDate: string,
    endDate?: string
  ): Promise<{totalCount: number; data: Array<CareTaskComplementary>}> {
    // Set url
    let url: string = `/entities/${entityId}/users/${userId}/care-tasks?startDate=${startDate}&includeComplementaryInformation=true&localize=true`;

    if (endDate) {
      url = `${url}&endDate=${endDate}`;
    }
    // Get Care Tasks from backend
    const response: AxiosResponse<Array<CareTaskComplementary>> =
      await BackendApiManager.instance.get<Array<CareTaskComplementary>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  public async GetEntityCareTask(
    entityId: string,
    careTaskRef: string
  ): Promise<CareTaskComplementary> {
    const url = `/entities/${entityId}/care-tasks/${careTaskRef}?includeComplementaryInformation=true&localize=true`;

    const response: AxiosResponse<CareTaskComplementary> =
      await BackendApiManager.instance.get<CareTaskComplementary>(url);

    return response.data;
  }

  async GetEntityCareTasks(
    entityId: string,
    startDate: string,
    endDate: string,
    config?: AxiosRequestConfig
  ): Promise<Array<CareTaskComplementary>> {
    // Set url
    const url: string = `/entities/${entityId}/care-tasks?startDate=${startDate}&endDate=${endDate}&includeComplementaryInformation=true&localize=true`;

    // Get Care Tasks from backend
    const response: AxiosResponse<Array<CareTaskComplementary>> =
      await BackendApiManager.instance.get<Array<CareTaskComplementary>>(url, config);

    // Return
    return response.data;
  }

  public async HandleCareTask(
    entityId: string,
    userId: string,
    careTaskRef: string,
    data: PutEntityCareTask
  ): Promise<CareTask> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-tasks/${careTaskRef}/handle`;

    // Get Care Task from backend
    const response: AxiosResponse<CareTask> = await BackendApiManager.instance.put<CareTask>(
      url,
      data
    );

    // Return
    return response.data;
  }

  public async ResetCareTask(
    entityId: string,
    userId: string,
    careTaskRef: string
  ): Promise<CareTask> {
    // Set url
    const url: string = `/entities/${entityId}/users/${userId}/care-tasks/${careTaskRef}/handle`;

    // Delete Care Task from backend
    const response: AxiosResponse<CareTask> =
      await BackendApiManager.instance.delete<CareTask>(url);

    // Return
    return response.data;
  }

  public async AddUnplannedTask(
    entityId: string,
    userId: string,
    executionDatetime: Date,
    activitiesSectorId: string,
    activitiesGroupId: string,
    activityTypeId: string,
    markedAsDone: boolean,
    notes?: string,
    entityShiftIds?: string[],
    taskNotDoneReason?: CareTaskNotDoneReason,
    data?: CareTaskData,
    feature?: CareTaskFeature,
    attachments?: Attachment[],
    nameConfigurationValue?: string
  ): Promise<CareTask> {
    const url: string = `/entities/${entityId}/care-tasks`;

    const response: AxiosResponse<CareTask> = await BackendApiManager.instance.post<CareTask>(url, {
      userId,
      executionDatetime,
      activitiesSectorId,
      activitiesGroupId,
      activityTypeId,
      nameConfigurationValue,
      markedAsDone,
      notes,
      entityShiftIds,
      taskNotDoneReason,
      data,
      feature,
      attachments,
    });

    return response.data;
  }
}
